// Imports statiques — les modules sont déjà bundlés, pas besoin d'import dynamique
import dexieService, { legalOwners } from '@/services/dexieService'
import authService from '@/services/authService'
import apiService from '@/services/api'
import { VUE_APP_API_URL } from '../../utils/env'

const state = {
  loading: true,
  steps: [
    { name: 'Vérification du stockage local', done: false },
    { name: 'Chargement des dictionnaires', done: false },
    { name: 'Chargement des teams', done: false },
    { name: 'Chargement des utilisateurs', done: false },
    { name: 'Chargement des mandators d\'assistance', done: false },
    { name: 'Chargement des mandators de gestion', done: false },
    { name: 'Chargement des propriétaires légaux', done: false },
    { name: 'Chargement des points d\'intervention', done: false },
    { name: 'Chargement des controls', done: false },
    { name: 'Chargement des propriétés', done: false },
    { name: 'Synchronisation', done: false },
    { name: 'Finalisation', done: false }
  ],
  currentStepIndex: 0,
  offline: false,
  detectedOffline: false,
  loadedAt: null,
  error: null,
  // Synchronisation ZIP en arrière-plan (sync périodique via export gzip)
  zipSync: false,
  zipSyncEntity: null,
  zipSyncExportDate: null,
  zipSyncLastSyncDate: null,
  // Phase courante du téléchargement ZIP ('downloading' | 'saving' | null)
  zipSyncPhase: null,
  // Progression en octets (phase downloading)
  zipSyncDownloadedBytes: 0,
  zipSyncTotalDownloadBytes: 0,
  // Nouvelles propriétés pour le chargement par batch
  propertiesTotal: 0,
  propertiesLoaded: 0,
  controlsTotal: 0,
  controlsLoaded: 0,
  currentBatch: 0,
  batchSize: 3000
}

const getters = {
  isLoading: (state) => state.loading,
  currentStep: (state) => {
    if (state.currentStepIndex < state.steps.length) {
      let stepName = state.steps[state.currentStepIndex].name
      
      // Ajouter le progrès pour l'étape de chargement des controls (à l'index 8)
      if (state.currentStepIndex === 8 && state.controlsTotal > 0) {
        const percentage = Math.round((state.controlsLoaded / state.controlsTotal) * 100)
        stepName += ` (${state.controlsLoaded}/${state.controlsTotal} - ${percentage}%)`
      }
      
      // Ajouter le progrès pour l'étape de chargement des propriétés (maintenant à l'index 9)
      if (state.currentStepIndex === 9 && state.propertiesTotal > 0) {
        const percentage = Math.round((state.propertiesLoaded / state.propertiesTotal) * 100)
        stepName += ` (${state.propertiesLoaded}/${state.propertiesTotal} - ${percentage}%)`
      }
      
      return stepName
    }
    return 'Chargement terminé'
  },
  allStepsCompleted: (state) => {
    return state.steps.every(step => step.done)
  },
  propertiesProgress: (state) => {
    if (state.propertiesTotal === 0) return 0
    return Math.round((state.propertiesLoaded / state.propertiesTotal) * 100)
  }
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_CURRENT_STEP(state, stepIndex) {
    state.currentStepIndex = stepIndex
  },
  COMPLETE_STEP(state, stepIndex) {
    if (state.steps[stepIndex]) {
      state.steps[stepIndex].done = true
    }
  },
  SET_OFFLINE(state, offline) {
    state.offline = offline
  },
  SET_DETECTED_OFFLINE(state, detected) {
    state.detectedOffline = detected
  },
  SET_LOADED_AT(state, timestamp) {
    state.loadedAt = timestamp
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  RESET_STEPS(state) {
    state.steps.forEach(step => {
      step.done = false
    })
    state.currentStepIndex = 0
    // Reset des propriétés de batch
    state.propertiesTotal = 0
    state.propertiesLoaded = 0
    state.currentBatch = 0
  },
  // Nouvelles mutations pour le chargement par batch
  SET_PROPERTIES_TOTAL(state, total) {
    state.propertiesTotal = total
  },
  SET_PROPERTIES_LOADED(state, loaded) {
    state.propertiesLoaded = loaded
  },
  INCREMENT_PROPERTIES_LOADED(state, count) {
    state.propertiesLoaded += count
  },
  SET_CONTROLS_TOTAL(state, total) {
    state.controlsTotal = total
  },
  SET_CONTROLS_LOADED(state, loaded) {
    state.controlsLoaded = loaded
  },
  INCREMENT_CONTROLS_LOADED(state, count) {
    state.controlsLoaded += count
  },
  SET_CURRENT_BATCH(state, batch) {
    state.currentBatch = batch
  },
  INCREMENT_CURRENT_BATCH(state) {
    state.currentBatch += 1
  },
  // Mutations pour la sync ZIP en arrière-plan
  SET_ZIP_SYNC(state, { active, entity = null, exportDate = null, lastSyncDate = null }) {
    state.zipSync = active
    state.zipSyncEntity = entity
    if (active) {
      state.zipSyncExportDate = exportDate
      state.zipSyncLastSyncDate = lastSyncDate
    } else {
      state.zipSyncExportDate = null
      state.zipSyncLastSyncDate = null
      state.zipSyncPhase = null
      state.zipSyncDownloadedBytes = 0
      state.zipSyncTotalDownloadBytes = 0
    }
  },
  SET_ZIP_SYNC_PHASE(state, phase) {
    state.zipSyncPhase = phase
  },
  SET_ZIP_SYNC_DOWNLOADED_BYTES(state, { downloadedBytes, totalDownloadBytes }) {
    state.zipSyncDownloadedBytes = downloadedBytes
    state.zipSyncTotalDownloadBytes = totalDownloadBytes
  }
}

const actions = {
  async checkLocalStorage({ commit, dispatch }) {
    commit('SET_LOADING', true)
    commit('SET_CURRENT_STEP', 0)
    
    try {
      // Fix perf : vérifier l'auth ET les données locales en parallèle
      const [isAuthenticated, hasLocalData] = await Promise.all([
        authService.isAuthenticated(),
        dexieService.hasLocalData()
      ])
      
      if (!isAuthenticated) {
        await new Promise(resolve => setTimeout(resolve, 500))
        commit('COMPLETE_STEP', 0)
        // Passer directement à l'étape suivante sans charger les dictionnaires
        commit('COMPLETE_STEP', 1) // Marquer l'étape dictionnaires comme terminée
        // BUG FIX : marquer toutes les étapes intermédiaires (teams, users, mandators,
        // legalOwners, interventionPoints, controls) comme terminées car elles sont sautées
        // quand l'utilisateur n'est pas authentifié. Sans ce marquage, allStepsCompleted
        // retourne false dans finalize() et SET_LOADING(false) n'est jamais appelé,
        // laissant l'écran de chargement bloqué indéfiniment.
        for (let i = 2; i <= 8; i++) {
          commit('COMPLETE_STEP', i)
        }
        await dispatch('loadPropertiesStep', { isAuthenticated: false })
        return
      }
      
      if (hasLocalData) {
        // IndexedDB déjà peuplé → finaliser immédiatement
        // Mais on vérifie quand même que les dictionnaires critiques sont présents
        // (cas où l'utilisateur les a vidés manuellement)
        try {
          await apiService.ensureDictionariesLoaded()
        } catch (e) {
          // Ne pas bloquer si l'API n'est pas disponible — on utilise les données locales
          console.warn('[initialLoading] Impossible de recharger les dictionnaires manquants:', e.message)
        }
        commit('COMPLETE_STEP', 0)
        for (let i = 1; i < 12; i++) {
          commit('COMPLETE_STEP', i)
        }
        commit('SET_LOADED_AT', new Date().toISOString())
        commit('SET_LOADING', false)
        return
      }
      
      // IndexedDB vide (premier lancement) → vérifier si l'API est accessible
      const isApiAccessible = await dispatch('checkApiAccessibility')
      
      if (!isApiAccessible) {
        // API inaccessible + IndexedDB vide : impossible de charger les données
        console.warn('API non accessible et aucune donnée locale disponible')
        commit('SET_ERROR', 'API non accessible. Vérifiez votre connexion réseau et rechargez la page.')
        commit('SET_LOADING', false)
        return
      }
      
      // Liste des dictionnaires attendus
      const expectedDictionaries = [
        'property_type',
        'visit_status',
        'answer_state', 
        'answer_yesno',
        'rentable',
        'unit',
        'sub_unit',
        'lease_type',
        'legal_owner_division',
        'mandator_division',
        'tenant_division',
        'parent_association_type',
        'progress'
      ]
      
      // Vérifier si tous les dictionnaires attendus sont présents et non vides
      const missingDictionaries = []
      for (const dictName of expectedDictionaries) {
        const cached = await dexieService.getDictionary(dictName)
        // Vérifier que cached existe et est un objet avant d'utiliser Object.keys
        if (!cached || typeof cached !== 'object' || Object.keys(cached).length === 0) {
          missingDictionaries.push(dictName)
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 500))
      commit('COMPLETE_STEP', 0)
      
      // Si des dictionnaires sont manquants, charger depuis l'API
      if (missingDictionaries.length > 0) {
        await dispatch('loadDictionaries', { isAuthenticated: true })
      } else {
        commit('COMPLETE_STEP', 1) // Marquer l'étape dictionnaires comme terminée
        await dispatch('loadTeams', { isAuthenticated: true })
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du stockage local:', error)
      commit('SET_ERROR', error.message)
    }
  },
  
  async loadDictionaries({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 1)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 1)
        await dispatch('loadPropertiesStep', { isAuthenticated: false })
        return
      }
      
      // Charger tous les dictionnaires manquants depuis l'API
      await apiService.ensureDictionariesLoaded()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 1)
      
      await dispatch('loadTeams', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des dictionnaires:', error)
      
      // Ne pas bloquer le processus pour une erreur de dictionnaire
      // Continuer avec le chargement des teams
      commit('COMPLETE_STEP', 1)
      await dispatch('loadTeams', { isAuthenticated })
    }
  },

  async loadTeams({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 2)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 2)
        await dispatch('loadPropertiesStep', { isAuthenticated: false })
        return
      }
      
      // Charger les teams depuis l'API
      await apiService.loadTeamsFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 2)
      
      await dispatch('loadUsers', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des teams:', error)
      
      // Ne pas bloquer le processus pour une erreur de teams
      // Continuer avec le chargement des utilisateurs
      commit('COMPLETE_STEP', 2)
      await dispatch('loadUsers', { isAuthenticated })
    }
  },

  async loadUsers({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 3)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 3)
        await dispatch('loadAssistanceMandators', { isAuthenticated: false })
        return
      }
      
      // Charger les utilisateurs depuis l'API
      await apiService.loadUsersFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 3)
      
      await dispatch('loadAssistanceMandators', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error)
      
      // Ne pas bloquer le processus pour une erreur d'utilisateurs
      // Continuer avec le chargement des mandators d'assistance
      commit('COMPLETE_STEP', 3)
      await dispatch('loadAssistanceMandators', { isAuthenticated })
    }
  },

  async loadAssistanceMandators({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 4)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 4)
        await dispatch('loadManagerMandators', { isAuthenticated: false })
        return
      }
      
      // Charger les mandators d'assistance depuis l'API
      await apiService.loadAssistanceMandatorsFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 4)
      
      await dispatch('loadManagerMandators', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des mandators d\'assistance:', error)
      
      // Ne pas bloquer le processus pour une erreur de mandators
      // Continuer avec le chargement des mandators de gestion
      commit('COMPLETE_STEP', 4)
      await dispatch('loadManagerMandators', { isAuthenticated })
    }
  },

  async loadManagerMandators({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 5)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 5)
        await dispatch('loadInterventionPoints', { isAuthenticated: false })
        return
      }
      
      // Charger les mandators de gestion depuis l'API
      await apiService.loadManagerMandatorsFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 5)
      
      await dispatch('loadLegalOwners', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des mandators de gestion:', error)
      
      // Ne pas bloquer le processus pour une erreur de mandators
      // Continuer avec le chargement des points d'intervention
      commit('COMPLETE_STEP', 5)
      await dispatch('loadInterventionPoints', { isAuthenticated })
    }
  },

  async loadLegalOwners({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 6)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 6)
        await dispatch('loadInterventionPoints', { isAuthenticated: false })
        return
      }
      
      // Vérifier si la table legalOwners est vide
      const cachedOwners = await legalOwners().toArray()
      
      if (cachedOwners && cachedOwners.length > 0) {
        // Si on a déjà des données en cache, passer à l'étape suivante
        commit('COMPLETE_STEP', 6)
        await dispatch('loadInterventionPoints', { isAuthenticated: true })
        return
      }
      
      // Charger les propriétaires légaux depuis l'API
      await apiService.loadLegalOwnersFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 6)
      
      await dispatch('loadInterventionPoints', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des propriétaires légaux:', error)
      
      // Ne pas bloquer le processus pour une erreur de propriétaires légaux
      // Continuer avec le chargement des points d'intervention
      commit('COMPLETE_STEP', 6)
      await dispatch('loadInterventionPoints', { isAuthenticated })
    }
  },

  async loadInterventionPoints({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 7)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 7)
        await dispatch('loadControls', { isAuthenticated: false })
        return
      }
      
      // Charger les points d'intervention depuis l'API
      await apiService.loadInterventionPointsFromAPI()
      
      await new Promise(resolve => setTimeout(resolve, 300))
      commit('COMPLETE_STEP', 7)
      
      await dispatch('loadControls', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des points d\'intervention:', error)
      
      // Ne pas bloquer le processus pour une erreur de points d'intervention
      // Continuer avec le chargement des controls
      commit('COMPLETE_STEP', 7)
      await dispatch('loadControls', { isAuthenticated })
    }
  },

  async loadControls({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 8)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 8)
        await dispatch('loadPropertiesStep', { isAuthenticated: false })
        return
      }
      
      // Vérifier combien de controls sont déjà en cache
      const localControlsCount = await dexieService.countControls()
      
      // Essayer de récupérer le nombre total de controls
      let totalControls = 0
      try {
        totalControls = await apiService.getControlsCount()
        commit('SET_CONTROLS_TOTAL', totalControls)
        
        // Si on a déjà tous les controls en cache, pas besoin de recharger
        if (localControlsCount >= totalControls && totalControls > 0) {
          commit('SET_CONTROLS_LOADED', localControlsCount)
          commit('COMPLETE_STEP', 8)
          await dispatch('loadPropertiesStep', { isAuthenticated: true })
          return
        }
      } catch (error) {
        console.warn('Impossible de récupérer le nombre de controls, chargement depuis l\'export quand même:', error)
      }
      
      // Charger tous les controls depuis le fichier d'export avec callback de progression
      const result = await apiService.loadAndSaveControlsFromExport((loaded, total) => {
        commit('SET_CONTROLS_TOTAL', total)
        commit('SET_CONTROLS_LOADED', loaded)
      })
      
      if (result.success) {
        commit('SET_CONTROLS_TOTAL', result.count)
        commit('SET_CONTROLS_LOADED', result.count)
        
      } else {
        console.error('Échec du chargement des controls depuis l\'export:', result.error)
        // Essayer de continuer quand même avec les données locales
        commit('SET_CONTROLS_LOADED', localControlsCount)
      }
      
      commit('COMPLETE_STEP', 8)
      await dispatch('loadPropertiesStep', { isAuthenticated: true })
    } catch (error) {
      console.error('Erreur lors du chargement des controls:', error)
      
      // Ne pas bloquer le processus pour une erreur de controls
      // Continuer avec le chargement des propriétés
      commit('COMPLETE_STEP', 8)
      await dispatch('loadPropertiesStep', { isAuthenticated })
    }
  },

  async loadPropertiesStep({ commit, dispatch }, { isAuthenticated } = {}) {
    commit('SET_CURRENT_STEP', 9)
    
    try {
      if (!isAuthenticated) {
        commit('COMPLETE_STEP', 9)
        await dispatch('synchronize')
        return
      }
      
      // Vérifier d'abord si on a besoin de charger les propriétés
      const shouldLoadProperties = await dispatch('checkIfPropertiesNeedLoading')
      
      if (shouldLoadProperties) {
        // Charger les propriétés par batch
        await dispatch('loadProperties')
      }
      
      commit('COMPLETE_STEP', 9)
      await dispatch('synchronize')
    } catch (error) {
      commit('SET_ERROR', error.message)
    }
  },

  async checkIfPropertiesNeedLoading({ commit }) {
    try {
      // Récupérer en parallèle le nombre API et le nombre local
      let apiPropertiesCount = 0
      let isApiReachable = false
      
      try {
        apiPropertiesCount = await apiService.getPropertiesCount()
        isApiReachable = true
      } catch (error) {
        isApiReachable = false
      }
      
      // Récupérer le nombre de propriétés dans Dexie
      const localPropertiesCount = await dexieService.countProperties()

      commit('SET_PROPERTIES_TOTAL', apiPropertiesCount)
      commit('SET_PROPERTIES_LOADED', localPropertiesCount)
      
      // Si l'API n'est pas joignable, utiliser les données locales si disponibles
      if (!isApiReachable) {
        if (localPropertiesCount > 0) {
          return false
        } else {
          throw new Error('Impossible de charger les données : API non joignable et aucune donnée locale')
        }
      }
      
      // Si l'API est joignable, comparer les nombres
      if (apiPropertiesCount === localPropertiesCount && localPropertiesCount > 0) {
        return false
      } else {
        return true
      }
      
    } catch (error) {
      console.error('Erreur lors de la vérification du besoin de chargement:', error)
      // En cas d'erreur, on préfère charger pour être sûr d'avoir les données
      return true
    }
  },
  
  async checkApiAccessibility() {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // Timeout de 10 secondes (premier lancement)
      
      // Simple vérification ping sans authentification
      const response = await fetch(`${VUE_APP_API_URL}/health`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        }
      })
      
      clearTimeout(timeoutId)
      
      // L'API est accessible si on obtient une réponse (même 404)
      return response.status !== 0
    } catch (error) {
      // Erreur réseau = API inaccessible
      if (error.name === 'AbortError' || error.message.includes('Failed to fetch')) {
        return false
      }
      // Pour les autres erreurs, supposer que l'API est accessible
      return true
    }
  },
  
  async synchronize({ commit, dispatch }) {
    commit('SET_CURRENT_STEP', 10)
    
    try {
      // Simuler la synchronisation finale
      await new Promise(resolve => setTimeout(resolve, 600))
      commit('COMPLETE_STEP', 10)
      
      await dispatch('finalize')
    } catch (error) {
      commit('SET_ERROR', error.message)
    }
  },

  async loadProperties({ commit, state }) {
    try {
      // Vérifier si on a déjà des propriétés en cache
      const existingPropertiesCount = await dexieService.countProperties()
      
      // Récupérer le nombre total de propriétés depuis l'API
      const totalProperties = state.propertiesTotal
      
      if (totalProperties === 0) {
        return
      }
      
      // Si on a déjà toutes les propriétés, pas besoin de charger
      if (existingPropertiesCount >= totalProperties && totalProperties > 0) {
        commit('SET_PROPERTIES_LOADED', existingPropertiesCount)
        return
      }
      
      // Charger toutes les properties depuis le fichier d'export avec callback de progression
      const result = await apiService.loadAndSavePropertiesFromExport((loaded, total) => {
        commit('SET_PROPERTIES_TOTAL', total)
        commit('SET_PROPERTIES_LOADED', loaded)
      })
      
      if (result.success) {
        commit('SET_PROPERTIES_TOTAL', result.count)
        commit('SET_PROPERTIES_LOADED', result.count)
        
      } else {
        console.error('Échec du chargement des properties depuis l\'export:', result.error)
        // Essayer de continuer quand même avec les données locales
        commit('SET_PROPERTIES_LOADED', existingPropertiesCount)
      }
      
    } catch (error) {
      console.error('Erreur lors du chargement des propriétés:', error)
      throw error
    }
  },
  
  async finalize({ commit }) {
    commit('SET_CURRENT_STEP', 11)
    
    try {
      // Simuler la finalisation
      await new Promise(resolve => setTimeout(resolve, 400))
      commit('COMPLETE_STEP', 11)
      
      // BUG FIX : toujours finaliser le chargement quand finalize() est atteint.
      // L'ancien guard "allStepsCompleted" causait un blocage indéfini si des étapes
      // intermédiaires n'étaient pas marquées done (ex: path !isAuthenticated ou
      // chemin d'erreur dans loadTeams/loadUsers/etc. court-circuitant vers loadPropertiesStep).
      // finalize() étant la dernière étape de la chaîne, son appel implique que
      // le processus de chargement est terminé, qu'importe le chemin emprunté.
      commit('SET_LOADED_AT', new Date().toISOString())
      commit('SET_LOADING', false)
    } catch (error) {
      commit('SET_ERROR', error.message)
    }
  },
  
  setOffline({ commit }, offline) {
    commit('SET_OFFLINE', offline)
  },
  
  setDetectedOffline({ commit }, detected) {
    commit('SET_DETECTED_OFFLINE', detected)
  },
  
  toggleOffline({ commit, state }) {
    commit('SET_OFFLINE', !state.offline)
  },
  
  reload({ commit, dispatch }) {
    commit('RESET_STEPS')
    commit('SET_ERROR', null)
    commit('SET_LOADING', true)
    dispatch('checkLocalStorage')
  },

  // Actions pour la sync ZIP en arrière-plan
  // payload peut être une string (entity) ou un objet { entity, exportDate, lastSyncDate }
  startZipSync({ commit }, payload = null) {
    if (payload && typeof payload === 'object') {
      commit('SET_ZIP_SYNC', { active: true, ...payload })
    } else {
      commit('SET_ZIP_SYNC', { active: true, entity: payload })
    }
  },

  stopZipSync({ commit }) {
    commit('SET_ZIP_SYNC', { active: false, entity: null })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
